import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { grey } from "@mui/material/colors";
import { useIdentity } from "../IdentityProvider";

import { tzAwareHumanizeTimeString } from "../../utilities/formatting";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";
import { useTrueBizApi } from "../../api";
import Loading from "../Loading";

const ROWS_PER_TABLE_PAGE = 10;
const DEFAULT_MATCH_PROFILE = {
  id: "default",
  name: "TrueBiz Default",
  created_at: "2022-06-04T15:13:07.000Z",
  updated_at: new Date().toISOString(),
  status: "live",
  enable_adverse_media: true,
  enable_negative_business_reviews: true,
  enable_paid_search_rate: true,
  enable_time_on_site: true,
  enable_country_traffic: true,
  enable_referring_site: true,
  enable_bounce_rate: true,
  enable_policies: true,
  red_country_codes: [],
  yellow_country_codes: [],
  red_mcc_codes: [],
  yellow_mcc_codes: [],
  red_content_categories: [],
  yellow_content_categories: [],
  is_account_default: false,
};

export default function MatchProfiles() {
  const theme = useTheme();

  const identity = useIdentity();

  const api = useTrueBizApi();

  const [matchProfiles, setMatchProfiles] = useState<
    APICompanyMatchProfile[] | null
  >(null);
  const [page, setPage] = useState<number>(0);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [defaultLoading, setDefaultLoading] = useState<boolean>(false);

  const loadCompanyMatchProfiles = useCallback(async () => {
    try {
      const companyMatchProfiles = await api.getCompanyMatchProfiles();
      setMatchProfiles(companyMatchProfiles);
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [api, setMatchProfiles]);

  useEffect(() => {
    loadCompanyMatchProfiles();
  }, [api, loadCompanyMatchProfiles]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleDelete = async (companyMatchProfileId: string) => {
    setDeleteLoading(true);
    try {
      await api.deleteCompanyMatchProfile(companyMatchProfileId);
      await loadCompanyMatchProfiles();
      setDeleteLoading(false);
    } catch (e) {
      Sentry.captureException(e);
      setDeleteLoading(false);
    }
  };

  const handleDefaultToggle = async (matchProfile: APICompanyMatchProfile) => {
    const newDefault = matchProfile.is_account_default ? null : matchProfile.id;

    setDefaultLoading(true);
    try {
      await api.setDefaultCompanyMatchProfile(newDefault);
      await loadCompanyMatchProfiles();
      setDefaultLoading(false);
    } catch (e) {
      Sentry.captureException(e);
      setDefaultLoading(false);
    }
  };

  if (matchProfiles === null) {
    return <Loading />;
  }

  // if list from server is empty, use a list with just the default
  const profiles =
    !!matchProfiles && matchProfiles.length > 0
      ? matchProfiles
      : [DEFAULT_MATCH_PROFILE];

  // sort by updated_at
  profiles.sort(function (a, b) {
    if (!a.updated_at || !b.updated_at) {
      return -1;
    }

    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  // place TrueBiz Default at the beginning
  const defaultIndex = profiles.findIndex(
    (profile) => profile.id === "default"
  );
  if (defaultIndex === -1) {
    profiles.unshift(DEFAULT_MATCH_PROFILE);
  } else if (defaultIndex > 0) {
    profiles.splice(defaultIndex, 1);
    profiles.unshift(DEFAULT_MATCH_PROFILE);
  }

  const isDefaultToggleDisabled = (matchProfileStatus: string | null) => {
    if (matchProfileStatus !== "live") {
      return true;
    }

    if (defaultLoading) {
      return true;
    }

    if (identity?.user?.accountName === "Flywire") {
      return true;
    }

    return false;
  };

  const accountHasDefault = matchProfiles.some(
    (mp) => mp.is_account_default === true
  );

  return (
    <Box sx={{ pb: 2 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        Risk Profiles
      </Typography>

      <Box sx={{ float: "right", mt: -7 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          component={NavLink}
          to="/risk_profile/create"
        >
          Create Profile
        </Button>
      </Box>

      <Paper elevation={2} sx={{ mt: 3, mb: 3, pr: 3, pl: 3 }}>
        <TableContainer component={Box}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Created at</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>ID</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Is default</strong>
                </TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles
                .slice(
                  page * ROWS_PER_TABLE_PAGE,
                  page * ROWS_PER_TABLE_PAGE + ROWS_PER_TABLE_PAGE
                )
                .map((matchProfile) => {
                  return (
                    <TableRow key={matchProfile.id}>
                      <TableCell>{matchProfile.name}</TableCell>
                      <TableCell align="center">
                        {tzAwareHumanizeTimeString(matchProfile.created_at)}
                      </TableCell>
                      <TableCell align="center">
                        {matchProfile.id === "default" ? "-" : matchProfile.id}
                      </TableCell>
                      <TableCell align="center">
                        {matchProfile.name === "TrueBiz Default" ? (
                          <Switch disabled checked={!accountHasDefault} />
                        ) : (
                          <Switch
                            disabled={isDefaultToggleDisabled(
                              matchProfile.status
                            )}
                            checked={matchProfile.is_account_default === true}
                            onClick={() => handleDefaultToggle(matchProfile)}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {matchProfile.name === "TrueBiz Default" ? (
                          <Tooltip title="View">
                            <IconButton
                              component={Link}
                              href={`${window.location.origin}/risk_profile/view/default`}
                            >
                              <VisibilityIcon
                                sx={{ color: theme.palette.primary.main }}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="View">
                              <IconButton
                                component={Link}
                                href={`${window.location.origin}/risk_profile/view/${matchProfile.id}`}
                              >
                                <VisibilityIcon
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <IconButton
                                component={Link}
                                href={`${window.location.origin}/risk_profile/edit/${matchProfile.id}`}
                              >
                                <EditIcon
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              </IconButton>
                            </Tooltip>

                            {matchProfile.id && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() =>
                                    matchProfile.id &&
                                    handleDelete(matchProfile.id)
                                  }
                                  disabled={deleteLoading}
                                >
                                  <DeleteIcon
                                    sx={{
                                      color: deleteLoading
                                        ? grey[600]
                                        : theme.palette.primary.main,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {profiles.length > ROWS_PER_TABLE_PAGE && (
          <TablePagination
            component="div"
            count={profiles.length}
            rowsPerPage={ROWS_PER_TABLE_PAGE}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[ROWS_PER_TABLE_PAGE]}
          />
        )}
      </Paper>
    </Box>
  );
}
